import apiClient from '../client';

export interface MessageAttachment {
  Id: number;
  MessageId: number;
  FileName: string;
  OriginalName: string;
  MimeType: string;
  FileSize: number;
  FilePath: string;
  CreatedAt: string;
}

export interface UserMessage {
  Id: number;
  SenderId: string;
  ReceiverId: string | null;
  GroupId: number | null;
  Content: string;
  CreatedAt: string;
  IsRead: boolean;
  ReadAt: string | null;
  Attachments?: MessageAttachment[];
}

export interface MessageWithSender extends UserMessage {
  SenderName: string;
}

export interface ConversationSummary {
  OtherUserId: string | null;
  OtherUserName: string;
  GroupId: number | null;
  IsGroup: boolean;
  LastMessage: string;
  LastMessageAt: string;
  UnreadCount: number;
  IsLastMessageFromMe: boolean;
}

export interface SendMessageData {
  receiverId: string;
  content: string;
  attachments?: File[];
}

export interface MessageGroup {
  Id: number;
  Name: string;
  CreatedBy: string;
  CreatedAt: string;
  MemberCount?: number;
}

export interface GroupMember {
  UserId: string;
  UserName: string;
  JoinedAt: string;
}

export interface GroupWithMembers extends MessageGroup {
  members: GroupMember[];
}

export interface CreateGroupData {
  name: string;
  memberIds: string[];
}

export const messageService = {
  // Direct messages
  sendMessage: async (data: SendMessageData): Promise<UserMessage> => {
    const formData = new FormData();
    formData.append('receiverId', data.receiverId);
    formData.append('content', data.content || '');

    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }

    const response: any = await apiClient.post('/messages', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getConversations: async (): Promise<ConversationSummary[]> => {
    const response: any = await apiClient.get('/messages/conversations');
    return response.data;
  },

  getConversation: async (otherUserId: string): Promise<MessageWithSender[]> => {
    const response: any = await apiClient.get(`/messages/conversation/${otherUserId}`);
    return response.data;
  },

  getUnreadCount: async (): Promise<number> => {
    const response: any = await apiClient.get('/messages/unread-count');
    return response.data.count;
  },

  markAsRead: async (otherUserId: string): Promise<void> => {
    await apiClient.put(`/messages/mark-read/${otherUserId}`);
  },

  // Group messages
  createGroup: async (data: CreateGroupData): Promise<GroupWithMembers> => {
    const response: any = await apiClient.post('/messages/groups', data);
    return response.data;
  },

  getGroup: async (groupId: number): Promise<GroupWithMembers> => {
    const response: any = await apiClient.get(`/messages/groups/${groupId}`);
    return response.data;
  },

  getGroupMessages: async (groupId: number): Promise<MessageWithSender[]> => {
    const response: any = await apiClient.get(`/messages/groups/${groupId}/messages`);
    return response.data;
  },

  sendGroupMessage: async (groupId: number, content: string, attachments?: File[]): Promise<UserMessage> => {
    const formData = new FormData();
    formData.append('content', content || '');

    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }

    const response: any = await apiClient.post(`/messages/groups/${groupId}/messages`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  markGroupAsRead: async (groupId: number): Promise<void> => {
    await apiClient.put(`/messages/groups/${groupId}/mark-read`);
  },

  deleteMessage: async (messageId: number): Promise<void> => {
    await apiClient.delete(`/messages/${messageId}`);
  },

  deleteConversation: async (otherUserId: string): Promise<void> => {
    await apiClient.delete(`/messages/conversation/${otherUserId}`);
  },

  getAttachmentUrl: (attachmentId: number): string => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
    const token = localStorage.getItem('token');
    return `${baseUrl}/messages/attachments/${attachmentId}${token ? `?token=${token}` : ''}`;
  },

  getAttachmentViewUrl: (attachmentId: number): string => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';
    const token = localStorage.getItem('token');
    return `${baseUrl}/messages/attachments/${attachmentId}?view=true${token ? `&token=${token}` : ''}`;
  },
};
